import { createContext, useContext, useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { api } from "@/lib/api";
import { useAuth } from "@/App";

// Legal entity + mailing address are platform config (LEGAL_NAME / POSTAL_ADDRESS) served by /api/site — never hard-coded here.
export const SUPPORT_EMAIL = "support@examray.com";
export const HOME_TITLE = "ExamRay — Certification Practice Exams with a Pass Guarantee.";
export const HOME_DESCRIPTION = "Full-length practice exams per certification, timed and weighted like the real thing. Every answer explained. Pass or your purchase price refunded.";

export const MONO = "'IBM Plex Mono', monospace";
export const CHIVO = "Chivo, sans-serif";

export function useMeta(title, description) {
  useEffect(() => {
    if (title) document.title = title;
    if (description) {
      let m = document.querySelector('meta[name="description"]');
      if (!m) { m = document.createElement("meta"); m.name = "description"; document.head.appendChild(m); }
      m.content = description;
    }
  }, [title, description]);
}

export function useMobile(bp = 760) {
  const [mob, setMob] = useState(() => typeof window !== "undefined" && window.innerWidth < bp);
  useEffect(() => {
    const f = () => setMob(window.innerWidth < bp);
    window.addEventListener("resize", f);
    return () => window.removeEventListener("resize", f);
  }, [bp]);
  return mob;
}

/* One source of truth for the public template: GET /api/site (published certs + standing code + guarantee). */
const SiteCtx = createContext(null);
export function SiteProvider({ children }) {
  const [site, setSite] = useState(null);
  useEffect(() => { api.get("/site").then((r) => setSite(r.data)).catch(() => setSite({ certs: [], catalog_layout: "panel", guarantee: null, access_months: null })); }, []);
  return <SiteCtx.Provider value={site}>{children}</SiteCtx.Provider>;
}
export const useSite = () => useContext(SiteCtx);

export const Wordmark = ({ size = 21, img = 32, to = "/", dark = false, testid = "header-logo" }) => (
  <Link to={to} data-testid={testid} className={`wordmark${dark ? " on-dark" : ""}`} style={{ fontSize: size, gap: Math.round(img / 4) }}>
    <img src="/ray.png" alt="" style={{ height: img }} />
    <span>Exam<em>Ray</em></span>
  </Link>
);

// Rule 12: no wave-bar or decorative motifs anywhere on the site.
export const WaveBar = () => null;

export function SiteHeader() {
  const { user, setUser } = useAuth();
  const navigate = useNavigate();
  const mob = useMobile();
  const logout = async () => {
    try { await api.post("/auth/logout"); } catch (e) { console.warn("logout failed", e); }
    setUser(false);
    navigate("/");
  };
  const signIn = mob
    ? { padding: "11px 0", border: "1px solid var(--ac)", color: "var(--ac-deep)", fontWeight: 600, borderRadius: 4, width: "100%", textAlign: "center", fontSize: 14, display: "block" }
    : { padding: "9px 18px", border: "1px solid var(--ac)", color: "var(--ac-deep)", fontWeight: 600, borderRadius: 4 };
  return (
    <>
      <div className="er-rule" />
      <header data-testid="site-header" style={{ borderBottom: "1px solid var(--hair)", minHeight: 64, display: "flex", flexWrap: "wrap", alignItems: "center", justifyContent: "space-between", gap: mob ? 12 : 16, padding: mob ? "14px 20px" : "14px 32px" }}>
        <Wordmark />
        <nav style={mob ? { display: "flex", flexWrap: "wrap", alignItems: "center", gap: 16, fontSize: 13, width: "100%" } : { display: "flex", alignItems: "center", gap: 26, fontSize: 14 }}>
          <Link className="nav-link" data-testid="nav-certifications" to="/#catalog">Certifications</Link>
          <Link className="nav-link" data-testid="nav-pricing" to="/pricing">Pricing</Link>
          <Link className="nav-link" data-testid="nav-guarantee" to="/guarantee">Pass guarantee</Link>
          {user ? (
            <>
              <Link className="nav-link" data-testid="nav-dashboard" to={user.is_admin ? "/admin" : "/dashboard"}>My exams</Link>
              <button className="link-btn" data-testid="nav-logout" onClick={logout} style={{ fontSize: "inherit" }}>Sign out</button>
            </>
          ) : (
            <Link data-testid="nav-login" to="/login" className="nav-link" style={signIn}>Sign in</Link>
          )}
        </nav>
      </header>
    </>
  );
}

export function SiteFooter() {
  const site = useSite();
  const certs = site?.certs || [];
  const col = { display: "flex", flexDirection: "column", gap: 7, marginTop: 12, fontSize: 13.5, alignItems: "flex-start" };
  return (
    <footer data-testid="site-footer" style={{ borderTop: "1px solid var(--hair)", padding: "44px 32px 0" }}>
      <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit,minmax(200px,1fr))", gap: 32 }}>
        <div>
          <Wordmark size={18} img={27} testid="footer-logo" />
          <p style={{ marginTop: 10, fontSize: 13.5, lineHeight: 1.6, color: "var(--ink-4)", maxWidth: "24em" }}>Certification practice exams with a pass-or-money-back guarantee.</p>
        </div>
        <div>
          <p className="label">CERTIFICATIONS</p>
          <div style={col} data-testid="footer-cert-list">
            {certs.map((c) => (
              <Link key={c.cert_slug} className="nav-link" data-testid={`footer-cert-${c.cert_slug}`} to={`/${c.cert_slug}`}>{c.cert_short}{c.exam_code ? ` ${c.exam_code}` : ""}</Link>
            ))}
            {site && certs.length === 0 && <span style={{ color: "var(--ink-5)" }}>More certifications in production</span>}
          </div>
        </div>
        <div>
          <p className="label">LEGAL</p>
          <div style={col}>
            <Link className="nav-link" to="/privacy">Privacy Policy</Link>
            <Link className="nav-link" to="/terms">Terms of Service</Link>
            <Link className="nav-link" to="/refund-policy">Refund Policy</Link>
            <Link className="nav-link" to="/guarantee">Pass Guarantee</Link>
          </div>
        </div>
        <div>
          <p className="label">SUPPORT</p>
          <div style={col}>
            <Link className="nav-link" to="/contact">Contact</Link>
            <span className="mono" style={{ fontSize: 13, color: "var(--ac-deep)" }}>{SUPPORT_EMAIL}</span>
          </div>
        </div>
      </div>
      <div className="mono" style={{ marginTop: 36, borderTop: "1px solid var(--hair-2)", padding: "18px 0", display: "flex", flexWrap: "wrap", gap: 8, justifyContent: "space-between", fontSize: 11, color: "var(--ink-5)" }}>
        <span data-testid="footer-entity">© {new Date().getFullYear()} {site?.legal?.name} · {site?.legal?.address}</span>
        <span>examray.com</span>
      </div>
    </footer>
  );
}

export function Page({ children, title, description, testid }) {
  useMeta(title, description);
  return (
    <div className="er-site" data-testid={testid}>
      <div className="frame">
        <SiteHeader />
        {children}
        <SiteFooter />
      </div>
    </div>
  );
}

/* URLs inside fixed copy (guarantee band, guarantee line) become links; the text itself is untouched. */
export function Linkify({ text }) {
  const parts = String(text || "").split(/(https?:\/\/[^\s]+?)(?=[.,;]?(?:\s|$))/);
  return <>{parts.map((p, i) => (/^https?:\/\//.test(p) ? <a key={i} href={p} data-testid="copy-link">{p.replace(/^https?:\/\/(www\.)?/, "")}</a> : p))}</>;
}

/* Numbered rows — the "01 / 02 / 03" list used by guarantee, capture, and cert pages. */
export function Steps({ items, big = false, testid }) {
  return (
    <div className="steps" data-testid={testid} style={{ display: "flex", flexDirection: "column" }}>
      {items.map((it, i) => (
        <div key={i} style={big ? { padding: "26px 0", display: "grid", gridTemplateColumns: "52px minmax(0,1fr)", gap: 20 } : { padding: "14px 0", display: "flex", gap: 14 }}>
          <span className="mono" style={{ fontSize: big ? 13 : 12, color: big ? "var(--ac-deep)" : "var(--ac)", paddingTop: big ? 0 : 3 }}>{String(i + 1).padStart(2, "0")}</span>
          {typeof it === "string"
            ? <p style={{ fontSize: 14.5, lineHeight: 1.6 }}><Linkify text={it} /></p>
            : <div>
                <p style={big ? { fontFamily: CHIVO, fontWeight: 700, fontSize: 23, letterSpacing: "-0.02em" } : { fontWeight: 600, fontSize: 15.5 }}>{it.title}</p>
                <p style={{ marginTop: big ? 10 : 5, fontSize: big ? 16 : 14, lineHeight: 1.65, color: big ? "var(--ink-3)" : "var(--ink-4)" }}>{it.body}</p>
              </div>}
        </div>
      ))}
    </div>
  );
}

export const money = (n) => `$${Number(n) % 1 === 0 ? Number(n).toFixed(0) : Number(n).toFixed(2)}`;
